import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Select } from "../ui/select";
import {
    PhoneCall, MessageSquare, Mail, Eye, EyeOff, Save, Loader2,
    ChevronDown, ChevronUp, PlugZap, Copy, CheckCircle, XCircle, Plus, Trash2, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import {
    getRetellConfig, updateRetellConfig,
    getAnthropicConfig, updateAnthropicConfig, listAnthropicModels,
    getMmdConfig, updateMmdConfig,
    getSmtpConfig, updateSmtpConfig,
    testIntegrationConnection
} from "../../services/crmServices/integrationService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { fetchEmailTemplates } from "../../services/crmServices/emailTemplateService";
import { serviceConstants } from "../../services/common/constants";
import { Link } from "react-router-dom";

// Full list of Retell disconnection reasons (per Retell API docs — DisconnectionReason enum)
const DISCONNECTION_REASONS = [
    // Normal call endings
    "user_hangup", "agent_hangup", "call_transfer", "call_take_over",
    "voicemail_reached", "ivr_reached", "inactivity", "max_duration_reached",
    // Call never connected
    "dial_no_answer", "dial_busy", "dial_failed", "invalid_destination",
    "user_declined", "marked_as_spam", "sip_routing_error",
    "telephony_provider_permission_denied", "telephony_provider_unavailable",
    // Account / capacity issues
    "concurrency_limit_reached", "no_concurrency_fallback", "no_valid_payment",
    "scam_detected", "registered_call_timeout",
    // Transfers & manual actions
    "transfer_bridged", "transfer_cancelled", "manual_stopped",
    // Errors
    "error_llm_websocket_open", "error_llm_websocket_lost_connection",
    "error_llm_websocket_runtime", "error_llm_websocket_corrupt_payload",
    "error_no_audio_received", "error_asr", "error_retell", "error_unknown",
    "error_user_not_joined"
];

// Sample data used for template previews
const PREVIEW_VARS = {
    firstName: "John",
    lastName: "Doe",
    phone: "+14155551234",
    email: "john.doe@example.com",
    country: "USA",
    city: "New York",
    source: "Google Ads",
    status: "New",
    message: "Here is the information you asked for during our call.",
    subject: "Information you requested",
    body: "Hi! As promised on our call, here are the details we discussed.\nLet me know if you have questions."
};

function renderPreview(template, htmlLineBreaks = false) {
    if (!template) return "";
    return String(template).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, key) => {
        let value = PREVIEW_VARS[key] ?? "";
        if (htmlLineBreaks && (key === "body" || key === "message")) {
            value = String(value).replace(/\n/g, "<br/>");
        }
        return value;
    });
}

const PLACEHOLDERS_HINT = "{{firstName}} {{lastName}} {{phone}} {{email}} {{country}} {{city}} {{source}} {{status}}";

function StatusBadge({ status }) {
    if (status === "active") {
        return (
            <Badge variant="outline" className="text-green-600 border-green-200 text-xs">
                <CheckCircle className="h-3 w-3 mr-1" /> Connected
            </Badge>
        );
    }
    if (status === "error") {
        return (
            <Badge variant="outline" className="text-red-600 border-red-200 text-xs">
                <XCircle className="h-3 w-3 mr-1" /> Error
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="text-gray-500 border-gray-200 text-xs">
            Not tested
        </Badge>
    );
}

function SectionHeader({ icon: Icon, iconBg, iconColor, title, subtitle, status, expanded, onToggle }) {
    return (
        <div
            className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white"
            onClick={onToggle}
        >
            <div className="flex items-center gap-3">
                <div className={`p-2 ${iconBg} rounded-lg`}>
                    <Icon className={`h-4 w-4 ${iconColor}`} />
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <StatusBadge status={status} />
                {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </div>
        </div>
    );
}

function SecretInput({ value, onChange, placeholder }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative mt-1">
            <Input
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="pr-10"
            />
            <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

function WebhookUrlRow({ label, url }) {
    const copy = () => {
        navigator.clipboard.writeText(url);
        toast.success("Copied to clipboard");
    };
    return (
        <div className="flex items-center justify-between gap-2 py-1.5">
            <div className="min-w-0">
                <p className="text-[11px] font-semibold text-gray-600">{label}</p>
                <p className="text-xs text-gray-500 font-mono truncate">{url}</p>
            </div>
            <Button variant="outline" size="sm" onClick={copy} className="shrink-0">
                <Copy className="h-3 w-3" />
            </Button>
        </div>
    );
}

function CopyBlock({ label, text }) {
    const copy = () => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };
    return (
        <div className="mt-1.5">
            <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold text-gray-600">{label}</p>
                <Button variant="outline" size="sm" onClick={copy} className="h-6 px-2">
                    <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
            </div>
            <pre className="text-[11px] text-gray-700 bg-white border border-gray-200 rounded-md p-2.5 whitespace-pre-wrap overflow-x-auto">{text}</pre>
        </div>
    );
}

const TOOL_SCHEMAS = {
    sendSms: `{
  "type": "object",
  "properties": {}
}`,
    sendEmail: `{
  "type": "object",
  "properties": {}
}`,
    scheduleCallback: `{
  "type": "object",
  "required": ["callback_time"],
  "properties": {
    "callback_time": {
      "type": "string",
      "description": "The date and time the customer wants to be called back, in ISO 8601 format with timezone, e.g. 2026-07-15T14:30:00+04:00"
    },
    "notes": {
      "type": "string",
      "description": "Short note about why the callback was scheduled and what to discuss"
    }
  }
}`
};

function RetellSetupGuide({ apiHost, emailTemplates = [] }) {
    const [open, setOpen] = useState(false);

    // Placeholders auto-filled from the lead — the AI does not provide these.
    const STANDARD_VARS = new Set([
        "firstName", "lastName", "phone", "email", "country", "city", "source",
        "status", "subject", "body", "message"
    ]);
    const customVarsOf = (t) => {
        const found = new Set();
        [t.subject, t.html].filter(Boolean).forEach((txt) => {
            (String(txt).match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) || []).forEach((m) => {
                const key = m.replace(/[{}\s]/g, "");
                if (!STANDARD_VARS.has(key)) found.add(key);
            });
        });
        return [...found];
    };

    // Build the send_email schema from the configured templates so the agent
    // can pick the right one by its key and supply any custom values it needs.
    const enabledTemplates = emailTemplates.filter((t) => t.enabled !== false);
    const allCustomVars = [...new Set(enabledTemplates.flatMap(customVarsOf))];
    const emailSchema = enabledTemplates.length > 0
        ? JSON.stringify({
            type: "object",
            required: ["template"],
            properties: {
                template: {
                    type: "string",
                    enum: enabledTemplates.map((t) => t.key),
                    description:
                        "Which email to send. " +
                        enabledTemplates
                            .map((t) => {
                                const cv = customVarsOf(t);
                                const need = cv.length ? ` (provide: ${cv.join(", ")})` : "";
                                return `"${t.key}"${t.isDefault ? " (default)" : ""}: ${t.description || t.name}${need}`;
                            })
                            .join(" | ")
                },
                ...Object.fromEntries(allCustomVars.map((v) => [v, {
                    type: "string",
                    description: `Value for {{${v}}} in the chosen template — fill from the conversation (leave empty if the template doesn't use it).`
                }]))
            }
        }, null, 2)
        : TOOL_SCHEMAS.sendEmail;

    const emailDescription = enabledTemplates.length > 0
        ? "Send one of our predefined emails to the customer's email address on file. Choose the right one via the 'template' parameter based on the conversation. You do not write the content — it is predefined in the CRM."
        : "Send our standard email to the customer's email address on file. The subject and content are predefined in the CRM — you do not write them. Use when the customer agrees to receive information by email.";

    const tools = [
        {
            name: "send_sms",
            description: "Send our standard SMS to the customer's phone number on file. The message content is predefined in the CRM — you do not write it. Use when the customer agrees to receive information by text message.",
            url: `${apiHost}/campaign/v1/tools/send-sms`,
            schema: TOOL_SCHEMAS.sendSms
        },
        {
            name: "send_email",
            description: emailDescription,
            url: `${apiHost}/campaign/v1/tools/send-email`,
            schema: emailSchema
        },
        {
            name: "schedule_callback",
            description: "Schedule a callback for the customer at a specific date and time. Use when the customer asks to be called back later. Always confirm the exact date and time with the customer first.",
            url: `${apiHost}/campaign/v1/tools/schedule-callback`,
            schema: TOOL_SCHEMAS.scheduleCallback
        }
    ];

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <span className="text-xs font-semibold text-gray-700">How to set this up in the Retell dashboard (step-by-step)</span>
                {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>
            {open && (
                <div className="p-4 space-y-5 text-xs text-gray-700 bg-white">
                    {/* Step 1 */}
                    <div>
                        <p className="font-semibold text-gray-900 mb-1">Step 1 — Webhook for call events (required)</p>
                        <ol className="list-decimal ml-4 space-y-1">
                            <li>In the Retell dashboard, open your <span className="font-medium">Agent</span> → scroll to <span className="font-medium">Webhook Settings</span> (or use account-level webhook under Settings → Webhooks).</li>
                            <li>Paste this URL into <span className="font-medium">Agent Level Webhook URL</span>:</li>
                        </ol>
                        <CopyBlock label="Webhook URL" text={`${apiHost}/campaign/v1/webhooks/retell`} />
                        <p className="mt-1.5 text-gray-500">This delivers call_started / call_ended / call_analyzed events. The CRM uses them to update campaign progress, save transcripts and recordings, and change lead statuses based on your outcome-mapping rules.</p>
                        <p className="mt-1 text-gray-500">If you use the <span className="font-medium">Live Call Monitor</span>, also add <span className="font-mono">transcript_updated</span> to the agent's Webhook Events list (not sent by default) — that's the live transcript stream the watchdog reads.</p>
                        <p className="mt-1 text-amber-600">Note: the URL must be reachable from the internet. On localhost, use a tunnel like ngrok and paste the tunnel URL instead.</p>
                    </div>

                    {/* Step 2 */}
                    <div>
                        <p className="font-semibold text-gray-900 mb-1">Step 2 — Custom Functions (optional tools for the agent)</p>
                        <p className="mb-2">For each tool below, in your agent's conversation flow / prompt editor click <span className="font-medium">Add Tool → Custom Function</span> and fill in exactly:</p>
                        <ul className="list-disc ml-4 space-y-1 mb-2">
                            <li><span className="font-medium">Method:</span> POST</li>
                            <li><span className="font-medium">Name / Description / URL / Parameters:</span> copy from the blocks below</li>
                            <li><span className="font-medium">"Payload: args only":</span> leave <span className="font-medium">OFF</span> (the CRM needs the call object to find the lead)</li>
                            <li><span className="font-medium">Headers / Query params:</span> leave empty (authentication is via the X-Retell-Signature header, automatic)</li>
                            <li><span className="font-medium">Timeout:</span> default is fine (the CRM responds in a few seconds)</li>
                        </ul>

                        {tools.map((tool) => (
                            <div key={tool.name} className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                <p className="font-semibold text-gray-900 font-mono">{tool.name}</p>
                                <CopyBlock label="Name" text={tool.name} />
                                <CopyBlock label="Description" text={tool.description} />
                                <CopyBlock label="URL" text={tool.url} />
                                <CopyBlock label="Parameters (JSON Schema)" text={tool.schema} />
                            </div>
                        ))}
                    </div>

                    {/* Step 3 */}
                    <div>
                        <p className="font-semibold text-gray-900 mb-1">Step 3 — Agent prompt tips</p>
                        <ul className="list-disc ml-4 space-y-1">
                            <li>Lead fields selected in the campaign are available as dynamic variables in the prompt, e.g. <span className="font-mono">{"{{firstName}}"}</span>, <span className="font-mono">{"{{phone}}"}</span>, <span className="font-mono">{"{{country}}"}</span>.</li>
                            <li>For <span className="font-mono">schedule_callback</span>, add the current date/time to the prompt so the agent can compute ISO timestamps, e.g. <span className="font-mono">{'"Current date and time: {{current_time}}"'}</span> (Retell provides <span className="font-mono">{"{{current_time}}"}</span> as a default variable).</li>
                            <li>Tell the agent when to use each tool, e.g. "If the customer asks for details in writing, use send_sms or send_email. If they ask to talk later, confirm a date and time, then use schedule_callback."</li>
                        </ul>
                    </div>

                    {/* Step 4 */}
                    <div>
                        <p className="font-semibold text-gray-900 mb-1">Step 4 — Post-call analysis (for outcome mapping)</p>
                        <ul className="list-disc ml-4 space-y-1">
                            <li>In the agent's <span className="font-medium">Post-Call Analysis</span> section you can define custom fields (e.g. <span className="font-mono">interested</span>: yes/no).</li>
                            <li>These arrive in the CRM as <span className="font-medium">custom analysis data</span> — map them to lead statuses in the campaign wizard's "Outcomes" step.</li>
                            <li>The built-in "Call successful" flag and disconnection reasons (e.g. <span className="font-mono">voicemail_reached</span>, <span className="font-mono">dial_no_answer</span>) can also be mapped.</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

export function AiCallerIntegrations() {
    const apiHost = serviceConstants.API_HOST_NAME;

    const [expanded, setExpanded] = useState({ retell: false, anthropic: false, mmd: false, smtp: false });
    const [saving, setSaving] = useState({ retell: false, anthropic: false, mmd: false, smtp: false });
    const [testing, setTesting] = useState({ retell: false, anthropic: false, mmd: false, smtp: false });

    const [retell, setRetell] = useState({
        enabled: false, hasApiKey: false, keyPrefix: "",
        statusMapping: [],
        toolStatusMapping: { sendSms: "", sendEmail: "", scheduleCallback: "" },
        callMonitor: { enabled: false, minCallSeconds: 60, checkIntervalSeconds: 20, model: "gpt-4o-mini", instructions: "" },
        status: "unknown"
    });
    const [retellKeyInput, setRetellKeyInput] = useState("");
    const [leadStatusOptions, setLeadStatusOptions] = useState([]);

    const [anthropic, setAnthropic] = useState({
        enabled: false, hasApiKey: false, keyPrefix: "", model: "claude-sonnet-4-5", status: "unknown"
    });
    const [anthropicKeyInput, setAnthropicKeyInput] = useState("");
    const [anthropicModels, setAnthropicModels] = useState([]);

    const loadAnthropicModels = async () => {
        try {
            const res = await listAnthropicModels();
            setAnthropicModels(res?.data || []);
        } catch {
            setAnthropicModels([]);
        }
    };

    const [mmd, setMmd] = useState({
        enabled: false, hasApiKey: false, keyPrefix: "", senderId: "", smsTemplate: "", status: "unknown"
    });
    const [mmdKeyInput, setMmdKeyInput] = useState("");

    const [smtp, setSmtp] = useState({
        enabled: false, host: "", port: 587, secure: false,
        username: "", hasPassword: false, fromEmail: "", fromName: "",
        emailSubject: "", emailHtml: "", status: "unknown"
    });
    const [smtpPasswordInput, setSmtpPasswordInput] = useState("");
    const [emailTemplates, setEmailTemplates] = useState([]);

    const loadEmailTemplates = async () => {
        try {
            const res = await fetchEmailTemplates();
            setEmailTemplates(res?.data || []);
        } catch {
            setEmailTemplates([]);
        }
    };

    const loadAll = async () => {
        try {
            const [retellRes, anthropicRes, mmdRes, smtpRes, statusRes] = await Promise.all([
                getRetellConfig(), getAnthropicConfig(), getMmdConfig(), getSmtpConfig(), fetchLeadStatuses()
            ]);
            if (retellRes?.data) setRetell(retellRes.data);
            if (anthropicRes?.data) {
                setAnthropic(anthropicRes.data);
                if (anthropicRes.data.hasApiKey) loadAnthropicModels();
            }
            if (mmdRes?.data) setMmd(mmdRes.data);
            if (smtpRes?.data) setSmtp(smtpRes.data);
            if (statusRes?.data) {
                setLeadStatusOptions(statusRes.data.map((s) => ({ label: s.label || s.value, value: s.label || s.value })));
            }
        } catch (err) {
            console.error("Failed to load AI Caller integration configs", err);
        }
    };

    useEffect(() => {
        loadAll();
        loadEmailTemplates();
    }, []);

    const handleSaveRetell = async () => {
        setSaving((s) => ({ ...s, retell: true }));
        try {
            const payload = {
                enabled: retell.enabled,
                statusMapping: (retell.statusMapping || []).filter((r) => r.newStatus),
                toolStatusMapping: retell.toolStatusMapping,
                callMonitor: retell.callMonitor
            };
            if (retellKeyInput.trim()) payload.apiKey = retellKeyInput.trim();
            const res = await updateRetellConfig(payload);
            if (res?.data) {
                setRetell(res.data);
                setRetellKeyInput("");
                toast.success("RetellAI settings saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save RetellAI settings");
        } finally {
            setSaving((s) => ({ ...s, retell: false }));
        }
    };

    const handleSaveAnthropic = async () => {
        setSaving((s) => ({ ...s, anthropic: true }));
        try {
            const payload = { enabled: anthropic.enabled, model: anthropic.model };
            if (anthropicKeyInput.trim()) payload.apiKey = anthropicKeyInput.trim();
            const res = await updateAnthropicConfig(payload);
            if (res?.data) {
                setAnthropic(res.data);
                setAnthropicKeyInput("");
                toast.success("Anthropic (Claude) settings saved");
                if (res.data.hasApiKey) loadAnthropicModels();
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save Anthropic settings");
        } finally {
            setSaving((s) => ({ ...s, anthropic: false }));
        }
    };

    const handleSaveMmd = async () => {
        setSaving((s) => ({ ...s, mmd: true }));
        try {
            const payload = { enabled: mmd.enabled, senderId: mmd.senderId, smsTemplate: mmd.smsTemplate };
            if (mmdKeyInput.trim()) payload.apiKey = mmdKeyInput.trim();
            const res = await updateMmdConfig(payload);
            if (res?.data) {
                setMmd(res.data);
                setMmdKeyInput("");
                toast.success("MMD SMS settings saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save MMD SMS settings");
        } finally {
            setSaving((s) => ({ ...s, mmd: false }));
        }
    };

    const handleSaveSmtp = async () => {
        setSaving((s) => ({ ...s, smtp: true }));
        try {
            const payload = {
                enabled: smtp.enabled,
                host: smtp.host,
                port: smtp.port,
                secure: smtp.secure,
                username: smtp.username,
                fromEmail: smtp.fromEmail,
                fromName: smtp.fromName,
                emailSubject: smtp.emailSubject,
                emailHtml: smtp.emailHtml
            };
            if (smtpPasswordInput.trim()) payload.password = smtpPasswordInput.trim();
            const res = await updateSmtpConfig(payload);
            if (res?.data) {
                setSmtp(res.data);
                setSmtpPasswordInput("");
                toast.success("SMTP settings saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save SMTP settings");
        } finally {
            setSaving((s) => ({ ...s, smtp: false }));
        }
    };

    const handleTest = async (provider) => {
        setTesting((t) => ({ ...t, [provider]: true }));
        try {
            const res = await testIntegrationConnection(provider);
            if (res?.data?.success) {
                toast.success(res.data.message || "Connection successful");
            } else {
                toast.error(res?.data?.message || "Connection failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Connection failed");
        } finally {
            setTesting((t) => ({ ...t, [provider]: false }));
            loadAll();
        }
    };

    return (
        <>
            {/* RetellAI Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                    icon={PhoneCall}
                    iconBg="bg-indigo-100"
                    iconColor="text-indigo-600"
                    title="RetellAI (AI Caller)"
                    subtitle={retell.enabled ? `Active${retell.hasApiKey ? " • key configured" : " • no API key"}` : "Disabled"}
                    status={retell.status}
                    expanded={expanded.retell}
                    onToggle={() => setExpanded((e) => ({ ...e, retell: !e.retell }))}
                />
                {expanded.retell && (
                    <div className="p-5 space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Enable RetellAI</Label>
                                <p className="text-xs text-gray-500 mt-0.5">Required to run AI call campaigns</p>
                            </div>
                            <Switch
                                checked={retell.enabled}
                                onCheckedChange={(v) => setRetell({ ...retell, enabled: v })}
                            />
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Retell API Key</Label>
                            <SecretInput
                                value={retellKeyInput}
                                onChange={setRetellKeyInput}
                                placeholder={retell.hasApiKey ? `${retell.keyPrefix}…  (leave blank to keep current)` : "key_..."}
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Get a key at <a href="https://dashboard.retellai.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">dashboard.retellai.com</a> (Settings → API Keys). Stored encrypted (AES-256-GCM). Also used to verify webhook signatures.
                            </p>
                        </div>

                        {/* Global call-outcome → lead-status mapping */}
                        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="text-sm font-medium text-gray-900">Lead Status Mapping</Label>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        When a call finishes, the first matching rule sets the lead's CRM status.
                                        Applies to all campaigns and test calls (campaign-specific rules take priority).
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setRetell({
                                        ...retell,
                                        statusMapping: [...(retell.statusMapping || []), { source: "disconnection_reason", key: "", value: "voicemail_reached", newStatus: "" }]
                                    })}
                                >
                                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Rule
                                </Button>
                            </div>

                            {(retell.statusMapping || []).length === 0 && (
                                <p className="text-xs text-gray-400">No rules yet. Example: disconnection reason "voicemail_reached" → status "Not Answered".</p>
                            )}

                            {(retell.statusMapping || []).map((rule, index) => {
                                const updateRule = (patch) => setRetell({
                                    ...retell,
                                    statusMapping: retell.statusMapping.map((r, i) => (i === index ? { ...r, ...patch } : r))
                                });
                                // rule.value holds one or more accepted values, comma-separated
                                const selectedReasons = String(rule.value || "").split(",").map((v) => v.trim()).filter(Boolean);
                                const toggleReason = (reason) => {
                                    const next = selectedReasons.includes(reason)
                                        ? selectedReasons.filter((r) => r !== reason)
                                        : [...selectedReasons, reason];
                                    updateRule({ value: next.join(",") });
                                };
                                return (
                                    <div key={index} className="bg-white p-2.5 rounded-md border border-gray-100 space-y-2">
                                        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_auto] gap-2 items-center">
                                            <Select
                                                options={[
                                                    { label: "Disconnection reason", value: "disconnection_reason" },
                                                    { label: "Call successful (true/false)", value: "call_successful" },
                                                    { label: "Reached voicemail (true/false)", value: "in_voicemail" },
                                                    { label: "User sentiment", value: "user_sentiment" },
                                                    { label: "Custom analysis field", value: "custom_analysis_data" }
                                                ]}
                                                value={rule.source}
                                                onChange={(v) => updateRule({
                                                    source: v,
                                                    value: (v === "call_successful" || v === "in_voicemail") ? "true" : ""
                                                })}
                                                placeholder="Check..."
                                            />
                                            <Select
                                                options={leadStatusOptions}
                                                value={rule.newStatus}
                                                onChange={(v) => updateRule({ newStatus: v })}
                                                placeholder="Set status to..."
                                                searchable
                                            />
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setRetell({
                                                    ...retell,
                                                    statusMapping: retell.statusMapping.filter((_, i) => i !== index)
                                                })}
                                            >
                                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                            </Button>
                                        </div>

                                        {rule.source === "disconnection_reason" && (
                                            <div>
                                                <p className="text-[11px] text-gray-500 mb-1">
                                                    Select one or more reasons — any of them triggers this status
                                                    {selectedReasons.length > 0 && <span className="text-gray-700 font-medium"> ({selectedReasons.length} selected)</span>}
                                                </p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {DISCONNECTION_REASONS.map((reason) => {
                                                        const isSelected = selectedReasons.includes(reason);
                                                        return (
                                                            <button
                                                                key={reason}
                                                                type="button"
                                                                onClick={() => toggleReason(reason)}
                                                                className={`px-2 py-0.5 rounded-full text-[11px] font-mono border transition-colors ${
                                                                    isSelected
                                                                        ? "bg-indigo-600 text-white border-indigo-600"
                                                                        : "bg-white text-gray-600 border-gray-200 hover:border-indigo-300"
                                                                }`}
                                                            >
                                                                {reason}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {(rule.source === "call_successful" || rule.source === "in_voicemail") && (
                                            <Select
                                                options={[{ label: "true", value: "true" }, { label: "false", value: "false" }]}
                                                value={rule.value}
                                                onChange={(v) => updateRule({ value: v })}
                                                placeholder="Value..."
                                                className="max-w-[200px]"
                                            />
                                        )}

                                        {rule.source === "user_sentiment" && (
                                            <Select
                                                options={[
                                                    { label: "Positive", value: "Positive" },
                                                    { label: "Negative", value: "Negative" },
                                                    { label: "Neutral", value: "Neutral" },
                                                    { label: "Unknown", value: "Unknown" }
                                                ]}
                                                value={rule.value}
                                                onChange={(v) => updateRule({ value: v })}
                                                placeholder="Sentiment..."
                                                className="max-w-[200px]"
                                            />
                                        )}

                                        {rule.source === "custom_analysis_data" && (
                                            <div className="flex gap-1.5">
                                                <Input
                                                    value={rule.key}
                                                    onChange={(e) => updateRule({ key: e.target.value })}
                                                    placeholder="Field name"
                                                    className="h-9 max-w-[200px]"
                                                />
                                                <Input
                                                    value={rule.value}
                                                    onChange={(e) => updateRule({ value: e.target.value })}
                                                    placeholder="Value(s), comma-separated"
                                                    className="h-9 max-w-[280px]"
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Tool action → lead status mapping */}
                        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Tool Actions → Lead Status</Label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    When the AI agent successfully uses a tool during the call, set the lead's status.
                                    A status set here wins over the post-call outcome mapping above.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {[
                                    { key: "sendSms", label: "SMS sent" },
                                    { key: "sendEmail", label: "Email sent" },
                                    { key: "scheduleCallback", label: "Callback scheduled" }
                                ].map((tool) => (
                                    <div key={tool.key}>
                                        <Label className="text-xs font-semibold text-gray-700">{tool.label}</Label>
                                        <Select
                                            options={[{ label: "— Leave unchanged —", value: "" }, ...leadStatusOptions]}
                                            value={retell.toolStatusMapping?.[tool.key] || ""}
                                            onChange={(v) => setRetell({
                                                ...retell,
                                                toolStatusMapping: { ...retell.toolStatusMapping, [tool.key]: v }
                                            })}
                                            placeholder="— Leave unchanged —"
                                            searchable
                                            className="mt-1"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Live call monitor (AI watchdog) */}
                        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="text-sm font-medium text-gray-900">Live Call Monitor (AI Watchdog)</Label>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        A ChatGPT supervisor reads the live transcript and hangs up calls that turned unproductive
                                        (customer trolling, illogical looping conversation, hostility, agent malfunction).
                                        Uses the OpenAI key from the AI Assistant section.
                                    </p>
                                </div>
                                <Switch
                                    checked={retell.callMonitor?.enabled || false}
                                    onCheckedChange={(v) => setRetell({ ...retell, callMonitor: { ...retell.callMonitor, enabled: v } })}
                                />
                            </div>

                            {retell.callMonitor?.enabled && (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Start judging after (seconds)</Label>
                                            <Input
                                                type="number"
                                                min="10"
                                                value={retell.callMonitor.minCallSeconds}
                                                onChange={(e) => setRetell({ ...retell, callMonitor: { ...retell.callMonitor, minCallSeconds: parseInt(e.target.value || "60", 10) } })}
                                                className="mt-1 bg-white"
                                            />
                                            <p className="text-[11px] text-gray-500 mt-1">Calls shorter than this are never judged</p>
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Check every (seconds)</Label>
                                            <Input
                                                type="number"
                                                min="10"
                                                value={retell.callMonitor.checkIntervalSeconds}
                                                onChange={(e) => setRetell({ ...retell, callMonitor: { ...retell.callMonitor, checkIntervalSeconds: parseInt(e.target.value || "20", 10) } })}
                                                className="mt-1 bg-white"
                                            />
                                            <p className="text-[11px] text-gray-500 mt-1">Minimum gap between checks per call</p>
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-gray-700">Model</Label>
                                            <Select
                                                options={[
                                                    { label: "gpt-4o-mini (fast, cheap — recommended)", value: "gpt-4o-mini" },
                                                    { label: "gpt-4o", value: "gpt-4o" },
                                                    { label: "gpt-5.4-mini", value: "gpt-5.4-mini" },
                                                    { label: "gpt-5.4", value: "gpt-5.4" }
                                                ]}
                                                value={retell.callMonitor.model}
                                                onChange={(v) => setRetell({ ...retell, callMonitor: { ...retell.callMonitor, model: v } })}
                                                className="mt-1"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-semibold text-gray-700">Extra rules (optional)</Label>
                                        <textarea
                                            value={retell.callMonitor.instructions}
                                            onChange={(e) => setRetell({ ...retell, callMonitor: { ...retell.callMonitor, instructions: e.target.value } })}
                                            placeholder="e.g. End the call if the customer asks about competitor products more than twice."
                                            rows={2}
                                            className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                                        />
                                    </div>
                                    <p className="text-[11px] text-amber-600">
                                        Requires the "transcript_updated" webhook event: in your Retell agent settings, add
                                        <span className="font-mono"> transcript_updated</span> to Webhook Events (it is not enabled by default).
                                        Ended calls get disconnection reason <span className="font-mono">manual_stopped</span> — you can map it to a lead status above.
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg">
                            <p className="text-xs font-semibold text-indigo-800 mb-1">Webhook URLs — paste these into your Retell dashboard</p>
                            <WebhookUrlRow label="Call events webhook (agent or account level)" url={`${apiHost}/campaign/v1/webhooks/retell`} />
                            <WebhookUrlRow label="Custom Function: Send SMS" url={`${apiHost}/campaign/v1/tools/send-sms`} />
                            <WebhookUrlRow label="Custom Function: Send Email" url={`${apiHost}/campaign/v1/tools/send-email`} />
                            <WebhookUrlRow label="Custom Function: Schedule Callback" url={`${apiHost}/campaign/v1/tools/schedule-callback`} />
                        </div>

                        <RetellSetupGuide apiHost={apiHost} emailTemplates={emailTemplates} />

                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            <Button
                                variant="outline"
                                onClick={() => handleTest("retell")}
                                disabled={testing.retell}
                            >
                                {testing.retell ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                                Test Connection
                            </Button>
                            <Button
                                onClick={handleSaveRetell}
                                disabled={saving.retell}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                                {saving.retell ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save RetellAI Settings
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Anthropic (Claude) Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                    icon={Sparkles}
                    iconBg="bg-orange-100"
                    iconColor="text-orange-600"
                    title="Anthropic Claude (AI Agent Editor)"
                    subtitle={anthropic.enabled ? `Active${anthropic.hasApiKey ? " • key configured" : " • no API key"}` : "Disabled"}
                    status={anthropic.status}
                    expanded={expanded.anthropic}
                    onToggle={() => setExpanded((e) => ({ ...e, anthropic: !e.anthropic }))}
                />
                {expanded.anthropic && (
                    <div className="p-5 space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Enable Claude</Label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Powers the AI Agents page: prompt editing suggestions, test-call analysis and fixes
                                </p>
                            </div>
                            <Switch
                                checked={anthropic.enabled}
                                onCheckedChange={(v) => setAnthropic({ ...anthropic, enabled: v })}
                            />
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Anthropic API Key</Label>
                            <SecretInput
                                value={anthropicKeyInput}
                                onChange={setAnthropicKeyInput}
                                placeholder={anthropic.hasApiKey ? `${anthropic.keyPrefix}…  (leave blank to keep current)` : "sk-ant-..."}
                            />
                            {anthropic.hasApiKey && !anthropicKeyInput && (
                                <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    A key is saved ({anthropic.keyPrefix}…). Leaving this field blank keeps it — you only type here to replace it.
                                </p>
                            )}
                            <p className="text-[11px] text-gray-500 mt-1">
                                Get a key at <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">console.anthropic.com</a> (Settings → API Keys). Stored encrypted (AES-256-GCM).
                            </p>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Model</Label>
                            <Select
                                options={
                                    anthropicModels.length > 0
                                        ? anthropicModels.map((m) => ({ label: m.displayName, value: m.id }))
                                        : [{ label: anthropic.model, value: anthropic.model }]
                                }
                                value={anthropic.model}
                                onChange={(v) => setAnthropic({ ...anthropic, model: v })}
                                searchable
                                className="mt-1"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                {anthropicModels.length > 0
                                    ? `Live list from your Anthropic account (${anthropicModels.length} models). Used for agent prompt editing and call analysis.`
                                    : "Save an API key first — the list of models available to your account will load automatically."}
                            </p>
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            <Button
                                variant="outline"
                                onClick={() => handleTest("anthropic")}
                                disabled={testing.anthropic}
                            >
                                {testing.anthropic ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                                Test Connection
                            </Button>
                            <Button
                                onClick={handleSaveAnthropic}
                                disabled={saving.anthropic}
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                                {saving.anthropic ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save Claude Settings
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* MMD SMS Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                    icon={MessageSquare}
                    iconBg="bg-teal-100"
                    iconColor="text-teal-600"
                    title="MMD SMS (MessageWhiz)"
                    subtitle={mmd.enabled ? `Active${mmd.hasApiKey ? " • key configured" : " • no API key"}` : "Disabled"}
                    status={mmd.status}
                    expanded={expanded.mmd}
                    onToggle={() => setExpanded((e) => ({ ...e, mmd: !e.mmd }))}
                />
                {expanded.mmd && (
                    <div className="p-5 space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Enable SMS Sending</Label>
                                <p className="text-xs text-gray-500 mt-0.5">Lets the AI agent send SMS to leads during calls</p>
                            </div>
                            <Switch
                                checked={mmd.enabled}
                                onCheckedChange={(v) => setMmd({ ...mmd, enabled: v })}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">MessageWhiz API Key</Label>
                                <SecretInput
                                    value={mmdKeyInput}
                                    onChange={setMmdKeyInput}
                                    placeholder={mmd.hasApiKey ? `${mmd.keyPrefix}…  (leave blank to keep current)` : "API key"}
                                />
                                <p className="text-[11px] text-gray-500 mt-1">
                                    From your <a href="https://sms.mmdsmart.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">sms.mmdsmart.com</a> account. Stored encrypted.
                                </p>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Sender Name / ID</Label>
                                <Input
                                    value={mmd.senderId}
                                    onChange={(e) => setMmd({ ...mmd, senderId: e.target.value })}
                                    placeholder="e.g. MyCompany"
                                    className="mt-1"
                                />
                                <p className="text-[11px] text-gray-500 mt-1">Shown as the "from" name on SMS messages</p>
                            </div>
                        </div>

                        {/* SMS template */}
                        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">SMS Template *</Label>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    This exact text is sent when the AI agent uses the send_sms tool — the AI does not write the message.
                                    Personalize with lead fields: <span className="font-mono">{PLACEHOLDERS_HINT}</span>
                                </p>
                            </div>
                            <textarea
                                value={mmd.smsTemplate}
                                onChange={(e) => setMmd({ ...mmd, smsTemplate: e.target.value })}
                                placeholder={"e.g. Hi {{firstName}}, thanks for your time on the call! Here is our info: https://example.com — The MyCompany Team"}
                                rows={3}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                            />
                            {mmd.smsTemplate.trim() && (
                                <div>
                                    <p className="text-[11px] font-semibold text-gray-600 mb-1">Preview (sample lead data)</p>
                                    <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{renderPreview(mmd.smsTemplate)}</p>
                                        <p className="text-[10px] text-gray-400 mt-1.5">{renderPreview(mmd.smsTemplate).length} characters{renderPreview(mmd.smsTemplate).length > 160 ? ` (${Math.ceil(renderPreview(mmd.smsTemplate).length / 153)} SMS segments)` : " (1 SMS segment)"}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            <Button
                                variant="outline"
                                onClick={() => handleTest("mmd")}
                                disabled={testing.mmd}
                            >
                                {testing.mmd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                                Test Connection
                            </Button>
                            <Button
                                onClick={handleSaveMmd}
                                disabled={saving.mmd}
                                className="bg-teal-600 hover:bg-teal-700 text-white"
                            >
                                {saving.mmd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save SMS Settings
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* SMTP Email Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <SectionHeader
                    icon={Mail}
                    iconBg="bg-orange-100"
                    iconColor="text-orange-600"
                    title="SMTP Email"
                    subtitle={smtp.enabled ? `Active${smtp.host ? ` • ${smtp.host}` : " • no host"}` : "Disabled"}
                    status={smtp.status}
                    expanded={expanded.smtp}
                    onToggle={() => setExpanded((e) => ({ ...e, smtp: !e.smtp }))}
                />
                {expanded.smtp && (
                    <div className="p-5 space-y-4">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Enable Email Sending</Label>
                                <p className="text-xs text-gray-500 mt-0.5">Lets the AI agent send emails to leads during calls</p>
                            </div>
                            <Switch
                                checked={smtp.enabled}
                                onCheckedChange={(v) => setSmtp({ ...smtp, enabled: v })}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="md:col-span-2">
                                <Label className="text-xs font-semibold text-gray-700">SMTP Host</Label>
                                <Input
                                    value={smtp.host}
                                    onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                                    placeholder="smtp.example.com"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Port</Label>
                                <Input
                                    type="number"
                                    value={smtp.port}
                                    onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value || "587", 10) })}
                                    placeholder="587"
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Use TLS on connect (port 465)</Label>
                                <p className="text-xs text-gray-500 mt-0.5">Off = STARTTLS (usual for port 587)</p>
                            </div>
                            <Switch
                                checked={smtp.secure}
                                onCheckedChange={(v) => setSmtp({ ...smtp, secure: v })}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Username</Label>
                                <Input
                                    value={smtp.username}
                                    onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                                    placeholder="user@example.com"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">Password</Label>
                                <SecretInput
                                    value={smtpPasswordInput}
                                    onChange={setSmtpPasswordInput}
                                    placeholder={smtp.hasPassword ? "••••••••  (leave blank to keep current)" : "Password"}
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">From Email</Label>
                                <Input
                                    value={smtp.fromEmail}
                                    onChange={(e) => setSmtp({ ...smtp, fromEmail: e.target.value })}
                                    placeholder="noreply@example.com"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-gray-700">From Name (optional)</Label>
                                <Input
                                    value={smtp.fromName}
                                    onChange={(e) => setSmtp({ ...smtp, fromName: e.target.value })}
                                    placeholder="My Company"
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        {/* Email templates — now managed on their own page */}
                        <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-lg">
                            <Label className="text-sm font-medium text-gray-900">Email Templates</Label>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Email content is now managed as multiple named templates (welcome, appointment scheduled,
                                company information, …). The AI caller picks one with its <span className="font-mono">send_email</span> tool.
                                {emailTemplates.length > 0 && (
                                    <> You currently have <span className="font-semibold">{emailTemplates.length}</span> template{emailTemplates.length === 1 ? "" : "s"}.</>
                                )}
                            </p>
                            <Link to="/settings/email-templates">
                                <Button variant="outline" size="sm" className="mt-2 bg-white">
                                    Manage Email Templates
                                </Button>
                            </Link>
                        </div>

                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                            <Button
                                variant="outline"
                                onClick={() => handleTest("smtp")}
                                disabled={testing.smtp}
                            >
                                {testing.smtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                                Test Connection
                            </Button>
                            <Button
                                onClick={handleSaveSmtp}
                                disabled={saving.smtp}
                                className="bg-orange-600 hover:bg-orange-700 text-white"
                            >
                                {saving.smtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save SMTP Settings
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
